import groupBy from 'lodash/groupBy';

import type { ScheduleLogRecord } from '~/types/responses';

// Bulk operations write one schedule_log row per affected schedule with the same action/member/diff —
// grouping them back into one entry avoids showing hundreds of near-identical rows.
const BULK_GROUP_WINDOW_MS = 10_000;

export interface ScheduleHistoryDiffField {
  field: string;
  before: string | null;
  after: string | null;
}

export interface ScheduleHistorySchedule {
  type: string;
  region: string;
  brand: string;
}

export interface ScheduleHistoryEntry {
  id: string;
  action: string;
  loggedAt: string;
  memberId: string;
  diffs: Array<ScheduleHistoryDiffField>;
  schedules: Array<ScheduleHistorySchedule>;
}

function toIso(value: string): string {
  return new Date(value).toISOString();
}

function marketingChannelsValue(marketingChannels: Array<string> | undefined): string {
  return marketingChannels?.length ? marketingChannels.join(', ') : '';
}

function diffRow(before: ScheduleLogRecord, after: ScheduleLogRecord): Array<ScheduleHistoryDiffField> {
  const diffs: Array<ScheduleHistoryDiffField> = [];

  function add(field: string, beforeValue: string | null, afterValue: string | null) {
    if (beforeValue !== afterValue) {
      diffs.push({ field, before: beforeValue, after: afterValue });
    }
  }

  add('start', toIso(before.start), toIso(after.start));
  add('end', toIso(before.end), toIso(after.end));
  add(
    'marketing_channels',
    marketingChannelsValue(before.marketing_channels),
    marketingChannelsValue(after.marketing_channels),
  );

  return diffs;
}

// Rows lack a stable id linking them back to one schedule, so consecutive rows sharing
// (type, region, brand) are treated as the history of a single schedule for diffing purposes.
function scheduleIdentityKey(log: ScheduleLogRecord): string {
  return `${log.type}-${log.region}-${log.brand}`;
}

interface DiffedRow {
  log: ScheduleLogRecord;
  diffs: Array<ScheduleHistoryDiffField>;
}

function diffRowsByIdentity(logs: Array<ScheduleLogRecord>): Array<DiffedRow> {
  const groups = groupBy(logs, scheduleIdentityKey);
  const diffed: Array<DiffedRow> = [];

  Object.values(groups).forEach((groupLogs) => {
    const sorted = [...groupLogs].sort((a, b) => new Date(a.logged_at).getTime() - new Date(b.logged_at).getTime());

    sorted.forEach((log, index) => {
      const previous = index > 0 ? sorted[index - 1] : undefined;
      diffed.push({
        log,
        // No previous row means no known baseline to diff against (true for the first row we
        // happen to have for an identity, e.g. history predating this feature, or a fresh INSERT).
        diffs: previous ? diffRow(previous, log) : [],
      });
    });
  });

  return diffed;
}

function diffSignature(diffs: Array<ScheduleHistoryDiffField>): string {
  return diffs
    .map((diff) => `${diff.field}:${diff.before ?? ''}=>${diff.after ?? ''}`)
    .sort()
    .join('|');
}

interface MergingEntry {
  signature: string;
  action: string;
  memberId: string;
  diffs: Array<ScheduleHistoryDiffField>;
  schedules: Array<ScheduleHistorySchedule>;
  firstId: number;
  firstLoggedAtMs: number;
  lastLoggedAtMs: number;
}

function mergeBulkChanges(diffedRows: Array<DiffedRow>): Array<MergingEntry> {
  const sorted = [...diffedRows].sort(
    (a, b) => new Date(a.log.logged_at).getTime() - new Date(b.log.logged_at).getTime(),
  );

  const merged: Array<MergingEntry> = [];
  let current: MergingEntry | null = null;

  sorted.forEach(({ log, diffs }) => {
    const loggedAtMs = new Date(log.logged_at).getTime();
    const signature = `${log.action}|${log.fk_member_id}|${diffSignature(diffs)}`;

    if (current?.signature === signature && loggedAtMs - current.lastLoggedAtMs <= BULK_GROUP_WINDOW_MS) {
      current.schedules.push({ type: log.type, region: log.region, brand: log.brand });
      current.lastLoggedAtMs = loggedAtMs;
    } else {
      if (current) {
        merged.push(current);
      }
      current = {
        signature,
        action: log.action,
        memberId: log.fk_member_id,
        diffs,
        schedules: [{ type: log.type, region: log.region, brand: log.brand }],
        firstId: log.id,
        firstLoggedAtMs: loggedAtMs,
        lastLoggedAtMs: loggedAtMs,
      };
    }
  });

  if (current) {
    merged.push(current);
  }

  return merged;
}

export function buildCampaignScheduleHistoryEntries(logs: Array<ScheduleLogRecord>): Array<ScheduleHistoryEntry> {
  const diffedRows = diffRowsByIdentity(logs);
  const merged = mergeBulkChanges(diffedRows).sort((a, b) => b.firstLoggedAtMs - a.firstLoggedAtMs);

  return merged.map((entry) => ({
    id: String(entry.firstId),
    action: entry.action,
    loggedAt: new Date(entry.firstLoggedAtMs).toISOString(),
    memberId: entry.memberId,
    diffs: entry.diffs,
    schedules: entry.schedules,
  }));
}
